import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { Card, ErrorBox, Loading, PageTitle, Stat } from "./ui";
import { fmtDate, inr, win } from "../lib/format";

export default function Wallet() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [amount, setAmount] = useState("");
  const [upiId, setUpiId] = useState("");
  const [wdStatus, setWdStatus] = useState({ state: "idle", message: "" });

  const load = () =>
    Promise.all([
      api.get("/wallet/"),
      api.get("/wallet/transactions"),
      api.get("/withdrawal/history"),
      api.get("/referral/stats"),
    ])
      .then(([wallet, txns, history, referral]) => {
        setData({
          wallet: wallet.data,
          txns: txns.data?.transactions || txns.data || [],
          history: history.data?.requests || history.data || [],
          referral: referral.data,
        });
      })
      .catch(() => setError("Could not load your wallet. Please refresh."));

  useEffect(() => {
    load();
  }, []);

  const requestWithdrawal = async (e) => {
    e.preventDefault();
    setWdStatus({ state: "sending", message: "" });
    try {
      await api.post("/withdrawal/request", {
        amount: Number(amount),
        payment_method: "upi",
        upi_id: upiId || undefined,
      });
      setWdStatus({ state: "done", message: "Withdrawal request submitted. It will be reviewed by the team." });
      setAmount("");
      await load();
    } catch (err) {
      setWdStatus({ state: "error", message: err.response?.data?.detail || "Withdrawal request failed. Please try again." });
    }
  };

  if (error) return <ErrorBox message={error} />;
  if (!data) return <Loading label="Loading your wallet" />;

  const { wallet, txns, history, referral } = data;
  const available = wallet.referral_bonus_available || 0;
  const minWd = referral.min_withdrawal_inr || 300;

  return (
    <div data-testid="wallet-page">
      <PageTitle title="Wallet" sub="Your balances, transactions and withdrawals." />

      <Card className="mt-8" data-testid="wallet-id-card">
        <p className="font-mono text-[10px] tracking-[0.25em] text-[#636B78] uppercase">Wallet ID</p>
        <p className="mt-2 font-mono text-xl font-semibold tracking-wider text-gold-gradient" data-testid="wallet-id">
          {user?.wallet_id || "—"}
        </p>
      </Card>

      <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat gold testId="wallet-win-total" label="WIN Token Balance" value={win(wallet.total_win_credits)} />
        <Stat testId="wallet-win-purchased" label="Purchased" value={win(wallet.win_credits_purchased)} />
        <Stat testId="wallet-win-earned" label="Earned" value={win(wallet.win_credits_earned)} />
        <Stat testId="wallet-inr" label="INR Balance" value={inr(wallet.inr_balance)} />
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <Card data-testid="withdrawal-card">
          <h2 className="font-display text-lg font-semibold text-white">Withdraw Referral Earnings</h2>
          <p className="mt-2 text-sm text-[#99A1AF]">
            Available to withdraw: <span className="font-mono text-white">{inr(available)}</span>
            <span className="text-[#636B78]"> · Minimum {inr(minWd)}</span>
          </p>
          <form onSubmit={requestWithdrawal} className="mt-5 space-y-4">
            <div>
              <label htmlFor="wd-amount" className="font-mono text-[10px] tracking-[0.25em] text-[#636B78] uppercase">Amount (INR)</label>
              <input
                id="wd-amount"
                type="number"
                min="1"
                step="1"
                required
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="withdrawal-amount-input"
                className="mt-2 w-full rounded-xl border border-white/10 bg-[#0B0B0F] px-4 py-3 text-sm text-white outline-none focus:border-[#D4AF37]/60"
              />
            </div>
            <div>
              <label htmlFor="wd-upi" className="font-mono text-[10px] tracking-[0.25em] text-[#636B78] uppercase">UPI ID</label>
              <input
                id="wd-upi"
                type="text"
                value={upiId}
                onChange={(e) => setUpiId(e.target.value)}
                placeholder="name@upi"
                data-testid="withdrawal-upi-input"
                className="mt-2 w-full rounded-xl border border-white/10 bg-[#0B0B0F] px-4 py-3 text-sm text-white placeholder:text-[#3A3F4A] outline-none focus:border-[#D4AF37]/60"
              />
            </div>
            {wdStatus.message && (
              <p className={`text-sm ${wdStatus.state === "error" ? "text-red-400" : "text-emerald-400"}`} data-testid="withdrawal-status">
                {wdStatus.message}
              </p>
            )}
            <button
              type="submit"
              disabled={wdStatus.state === "sending"}
              data-testid="withdrawal-submit-button"
              className="rounded-full bg-[#D4AF37] px-6 py-3 text-sm font-semibold text-[#08080A] transition-colors hover:bg-[#F3E5AB] disabled:opacity-60"
            >
              {wdStatus.state === "sending" ? "Submitting…" : "Request Withdrawal"}
            </button>
          </form>

          {history.length > 0 && (
            <div className="mt-6 border-t border-white/[0.08] pt-5">
              <p className="font-mono text-[10px] tracking-[0.25em] text-[#636B78] uppercase">Withdrawal History</p>
              <div className="mt-3 space-y-2">
                {history.map((h) => (
                  <div key={h.id} className="flex items-center justify-between text-sm">
                    <span className="text-[#99A1AF]">{fmtDate(h.created_at)}</span>
                    <span className="font-mono text-white">{inr(h.amount)}</span>
                    <span className={`rounded-full px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${
                      h.status === "approved" ? "bg-emerald-400/10 text-emerald-400" :
                      h.status === "rejected" ? "bg-red-400/10 text-red-400" : "bg-amber-400/10 text-amber-400"
                    }`}>{h.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        <Card data-testid="transactions-card">
          <h2 className="font-display text-lg font-semibold text-white">Transaction History</h2>
          {txns.length === 0 ? (
            <p className="mt-4 text-sm text-[#636B78]">No transactions yet.</p>
          ) : (
            <div className="mt-4 max-h-[420px] divide-y divide-white/[0.06] overflow-y-auto pr-1">
              {txns.map((t) => (
                <div key={t.id} className="flex items-center justify-between py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm text-white">{t.description || t.type}</p>
                    <p className="mt-0.5 font-mono text-[11px] text-[#636B78]">{fmtDate(t.created_at)} · {t.type}</p>
                  </div>
                  <span className={`ml-4 shrink-0 font-mono text-sm font-semibold ${Number(t.amount) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {Number(t.amount) >= 0 ? "+" : ""}
                    {t.currency === "INR" ? inr(t.amount) : win(t.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
